import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import StoryCard from "@/components/StoryCard";
import StoryDetail from "@/components/StoryDetail";
import { Loader2, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Feed() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/feed${forceRefresh ? "?refresh=true" : ""}`);
      setStories(data.stories || []);
    } catch (e) {
      setError("Couldn't load feed. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendBriefNow = async () => {
    setSending(true);
    try {
      await api.post("/brief/send-now");
      toast.success("Your Daily Brief is on the way to your inbox.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send brief");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="feed-page">
      <header className="border-b border-gray-200 px-4 py-5 sm:px-8 sm:py-6 sticky top-14 lg:top-0 bg-white z-20">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gray-500 truncate">
              Edition · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-none tracking-tight mt-2">
              Your Feed
            </h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <button
              data-testid="send-brief-now-button"
              onClick={sendBriefNow}
              disabled={sending}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 border border-gray-300 hover:border-[#0a0a0a] disabled:opacity-60 px-4 py-2.5 sm:py-2 text-xs font-mono uppercase tracking-widest rounded-md"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} strokeWidth={1.5} />}
              <span className="hidden sm:inline">Email brief now</span>
              <span className="sm:hidden">Email brief</span>
            </button>
            <button
              onClick={() => load(true)}
              data-testid="refresh-feed-button"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 border border-gray-300 hover:border-[#0a0a0a] px-4 py-2.5 sm:py-2 text-xs font-mono uppercase tracking-widest rounded-md"
            >
              <RefreshCw size={14} strokeWidth={1.5} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <div className="px-0 sm:px-2 lg:px-8 py-2">
        {loading && (
          <div className="flex items-center gap-3 px-6 py-12 text-gray-500">
            <Loader2 size={18} className="animate-spin" />
            <span className="font-mono text-xs uppercase tracking-widest">Scanning the last 72 hours…</span>
          </div>
        )}
        {!loading && error && (
          <div className="px-6 py-12 text-red-600">{error}</div>
        )}
        {!loading && !error && stories.length === 0 && (
          <div className="px-6 py-12 text-gray-500 font-mono text-xs uppercase tracking-widest">
            No fresh stories. Try adjusting your categories.
          </div>
        )}
        <div>
          {stories.map((s, i) => (
            <StoryCard key={s.id} story={s} index={i} onSelect={setSelected} />
          ))}
        </div>
      </div>

      {selected && (
        <StoryDetail story={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
